import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CurrencyCircleDollar, TrendUp, Package, Warning, Warehouse, ArrowRight, ShoppingCart, FileText, CalendarBlank } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { fetchMetrics(); fetchStockAlerts(); }, []);

  const fetchMetrics = async () => {
    try { setMetrics((await axios.get(`${API}/analytics/dashboard`)).data); }
    catch (e) { console.warn("Dashboard:", e.message); }
    finally { setLoading(false); }
  };
  const fetchStockAlerts = async () => {
    try { setStockAlerts((await axios.get(`${API}/stock/alerts`)).data); }
    catch (e) { console.warn("Alerts:", e.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t('nav_dashboard')}</h2>
        <p className="text-xs md:text-sm text-zinc-500 mt-1">{t('dash_overview')}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="metrics-cards">
        {[
          { key: "dash_estimated_profit", value: formatCurrency(metrics?.total_profit_month), icon: TrendUp, color: "text-green-600", bg: "bg-green-50", testid: "total-profit" },
          { key: "dash_total_cost", value: formatCurrency(metrics?.total_cost), icon: CurrencyCircleDollar, color: "text-red-500", bg: "bg-red-50", testid: "total-cost" },
          { key: "dash_products", value: metrics?.total_products || 0, icon: Package, color: "text-blue-600", bg: "bg-blue-50", testid: "total-products" },
          { key: "dash_ingredients_count", value: metrics?.total_ingredients || 0, icon: Warehouse, color: "text-zinc-600", bg: "bg-zinc-100", testid: "total-ingredients" },
        ].map((kpi) => (
          <Card key={kpi.key} className="border-zinc-200 shadow-sm rounded-sm card-hover">
            <CardContent className="p-3 md:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-wider font-semibold text-zinc-500">{t(kpi.key)}</p>
                  <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1 md:mt-2" data-testid={kpi.testid}>{kpi.value}</p>
                </div>
                <div className={`w-7 h-7 md:w-9 md:h-9 rounded-sm ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={`${kpi.color} md:hidden`} weight="bold" />
                  <kpi.icon size={18} className={`${kpi.color} hidden md:block`} weight="bold" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="lg:col-span-1 border-zinc-200 shadow-sm rounded-sm" data-testid="stock-alerts-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-zinc-900">
              <Warning size={16} className="text-amber-500" weight="bold" /> {t('dash_stock_alerts')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stockAlerts.length === 0 ? (
              <div className="text-center py-6">
                <Warehouse size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-sm font-medium text-zinc-700">{t('dash_all_good')}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{t('dash_no_shortage')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stockAlerts.slice(0, 5).map((a) => (
                  <Alert key={a.ingredient_id} className={`border ${a.status === "zerado" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                    data-testid={`stock-alert-${a.ingredient_id}`}>
                    <AlertDescription className="text-sm font-medium">
                      {a.ingredient_name}: {a.current_quantity} {a.unit}
                      {a.minimum_stock > 0 && <span className="text-xs ml-1 opacity-70">(min: {a.minimum_stock} {a.unit})</span>}
                    </AlertDescription>
                  </Alert>
                ))}
                <Button variant="ghost" size="sm" className="w-full text-zinc-600 hover:bg-zinc-100 mt-1" onClick={() => navigate("/stock")} data-testid="go-to-stock-btn">
                  {t('nav_stock')} <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-zinc-200 shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-zinc-900">{t('dash_quick_access')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
              {[
                { label: t('dash_new_sheet'), desc: t('dash_create_recipe'), icon: FileText, path: "/technical-sheets", bg: "bg-zinc-50" },
                { label: t('dash_register_purchase'), desc: t('dash_stock_entry'), icon: Warehouse, path: "/stock", bg: "bg-green-50" },
                { label: t('dash_shopping_list'), desc: t('dash_gen_shopping'), icon: ShoppingCart, path: "/shopping-list", bg: "bg-blue-50" },
                { label: t('dash_events'), desc: t('dash_manage_events'), icon: CalendarBlank, path: "/events", bg: "bg-amber-50" },
              ].map((item) => (
                <button key={item.path} onClick={() => navigate(item.path)}
                  className="flex items-center gap-3 p-3 md:p-4 rounded-sm border border-zinc-200 hover:border-zinc-300 hover:shadow-sm transition-all text-left group"
                  data-testid={`quick-${item.path.replace('/', '')}`}>
                  <div className={`w-8 h-8 md:w-9 md:h-9 rounded-sm ${item.bg} flex items-center justify-center flex-shrink-0`}>
                    <item.icon size={16} className="text-zinc-700 md:hidden" weight="bold" />
                    <item.icon size={18} className="text-zinc-700 hidden md:block" weight="bold" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-semibold text-zinc-900">{item.label}</p>
                    <p className="text-[10px] md:text-xs text-zinc-500 hidden sm:block">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
